/** Supporting documents on a record: list, open (hash-checked) and attach. */
import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, Upload } from 'lucide-react';
import { api } from '../lib/api';
import { openFile, uploadFile } from '../lib/files';
import { dateTime } from '../lib/format';
import { Alert, Button, Card } from './ui';

interface FileRow { id: string; filename: string; size: number; createdAt: string }

export function Attachments({ entity, entityId, canAttach }: { entity: string; entityId: string; canAttach: boolean }): JSX.Element {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const key = ['attachments', entity, entityId];
  const list = useQuery({ queryKey: key, queryFn: () => api<FileRow[]>(`/files?entity=${entity}&entityId=${entityId}`) });
  const upload = useMutation({ mutationFn: (f: File) => uploadFile(f, entity, entityId), onSuccess: () => void qc.invalidateQueries({ queryKey: key }) });
  return (
    <Card
      title="Attachments"
      description="Receipts, contracts and other support. Each file is fingerprinted and checked when opened."
      actions={canAttach && (
        <>
          <input ref={input} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
          <Button size="sm" variant="secondary" busy={upload.isPending} onClick={() => input.current?.click()}><Upload aria-hidden="true" className="h-4 w-4" /> Attach file</Button>
        </>
      )}
      className="mt-4"
    >
      {upload.error && <div className="mb-3"><Alert tone="critical">{(upload.error as Error).message}</Alert></div>}
      {list.data?.length ? (
        <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
          {list.data.map((f) => (
            <li key={f.id} className="flex items-center justify-between py-2">
              <button className="inline-flex items-center gap-2 font-medium text-brand-700 hover:underline dark:text-brand-100" onClick={() => void openFile(`/files/${f.id}`)}><Paperclip aria-hidden="true" className="h-4 w-4" />{f.filename}</button>
              <span className="text-xs text-slate-500">{(f.size / 1024).toFixed(0)} KB · {dateTime(f.createdAt)}</span>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-slate-500">No attachments.</p>}
    </Card>
  );
}
